import { QASearchSource } from '@next-space/fe-api-idl';
import { EditorContent, useEditor } from '@tiptap/react';
import type { JSONContent } from '@tiptap/core';
import isHotkey from 'is-hotkey';
import { memo, useCallback, useEffect, useMemo, useRef } from 'react';
import { findAIModel } from '../../ai-const';
import { cx } from '../../cx';
import { Icon } from '../../icon-svg';
import { MENTION_SUGGESTION_CLASS } from '../const';
import { destroyEditor, getEditor, setEditor } from '../hooks/cache';
import { useConversationContext } from '../hooks/context';
import {
  getQAQueryOption,
  useChatStatus,
  useConversationEdit,
  useConversationTools,
  useQAQueryOption,
} from '../hooks/hook';
import { getConversationEdit, searchPageStore } from '../hooks/store';
import { ChatStatus, ChatUIStyle, EditorNodeType, ReferencePageType } from '../hooks/types';
import { useChatTranslation } from '../hooks/use-i18n';
import { getPageTitleInfo } from '../hooks/use-search';
import { SourcesOptionType, useOpenSelectAIModel, useOpenSourcesOption } from './chat-sources';
import { createTiptapExtensions } from './tiptap-input';
import { isMobile } from 'react-device-detect';
import { Tooltip } from '../../tooltip';
import { ChatType } from '../types';
import { isBuildIn } from '../../build-in';

export const smallInput = 'max-h-14';
export const bigInput = 'max-h-60';

export const ChatInput = memo(() => {
  const { t } = useChatTranslation();
  const { style, conversationId, spaceId } = useConversationContext();
  const status = useChatStatus(conversationId);
  const { setKeywords, submitQuestion, removeSearchBlocks, addSearchBlocks } =
    useConversationTools();
  const { keywords } = useConversationEdit();

  const extensions = useMemo(() => createTiptapExtensions({ spaceId }), [spaceId]);
  const lastTextMentionSet = useRef<Set<string>>();
  const findMentions = useCallback((content: JSONContent[]) => {
    const mentionIdSet: Set<string> = new Set();
    content.forEach((c) => {
      if (c.type === 'paragraph') {
        c.content?.forEach((v) => {
          if (v.type === 'mention') {
            v.attrs?.id && mentionIdSet.add(v.attrs?.id);
          }
        });
      }
    });
    return mentionIdSet;
  }, []);

  const editor = useEditor(
    {
      extensions,
      content: getConversationEdit(conversationId).keywords,
      onUpdate: ({ editor }) => {
        const text = editor.getText();
        setKeywords(text);
        // 检查是否有@页面被删除
        const mentionIdSet = findMentions(editor.getJSON().content ?? []);
        if (lastTextMentionSet.current == null) {
          lastTextMentionSet.current = new Set();
          mentionIdSet.forEach((v) => {
            lastTextMentionSet.current?.add(v);
          });
        } else {
          // 跟上一次对比，检查是否有@页面被删除
          lastTextMentionSet.current.forEach((v) => {
            if (!mentionIdSet.has(v)) {
              removeSearchBlocks(v);
            }
          });
          //   //如果是回退操作，需要加回去
          mentionIdSet.forEach((v) => {
            if (!lastTextMentionSet.current?.has(v)) {
              addSearchBlocks({
                uuid: v,
                //TODO 未来支持文件的话这里要获取uuid对应的type
                type: ReferencePageType.PAGE,
              });
            }
          });
          lastTextMentionSet.current = mentionIdSet;
        }
      },
      editorProps: {
        handleKeyDown: (view, event) => {
          const { state } = view;
          const { from } = state.selection;
          const isInCodeBlock =
            state.doc.resolve(from).parent.type.name === EditorNodeType.CODE_BLOCK;

          // Check if mention suggestion menu is active
          const isSuggestionActive = document.querySelector(`.${MENTION_SUGGESTION_CLASS}`);
          if (isSuggestionActive) {
            return false;
          }

          if (isInCodeBlock) {
            return false;
          }

          // Handle backspace and delete when content is empty
          if (
            (isHotkey('backspace', event) || isHotkey('delete', event)) &&
            state.doc.content.size === 2
          ) {
            event.preventDefault();
            const conversation = getConversationEdit(conversationId);
            const currentSearchBlocks = getQAQueryOption(conversation?.qaQueryOption).searchBlocks;
            if (currentSearchBlocks.length > 0) {
              const lastBlock = currentSearchBlocks[currentSearchBlocks.length - 1];
              if (lastBlock?.uuid) {
                removeSearchBlocks(lastBlock.uuid);
              }
            }
            return true;
          }

          if (
            isHotkey('enter', event) &&
            !event.altKey &&
            !event.shiftKey &&
            !event.ctrlKey &&
            !event.metaKey
          ) {
            event.preventDefault();
            void submitQuestion();
            return true;
          }
          return false;
        },
        attributes: {
          placeholder: t('chatInputPlaceholder'),
          style: 'min-height: 62px',
        },
      },
    },
    [conversationId]
  );

  useEffect(() => {
    if (editor) {
      setEditor(conversationId, editor);
      // Set cursor to the end of content after initialization
      editor.commands.setTextSelection(editor.state.doc.content.size);
    }
    return () => {
      destroyEditor(conversationId);
    };
  }, [editor, conversationId]);

  useEffect(() => {
    if (!editor) return;
    if (status === ChatStatus.DEFAULT && !editor.isFocused) {
      editor.commands.focus();
    }
  }, [editor, conversationId, status]);

  if (!editor) {
    return null;
  }

  return (
    <div className="w-full relative text-t2">
      <SelectPages />
      <div
        className={cx(
          'overflow-auto w-full text-t1',
          style === ChatUIStyle.DEFAULT ? bigInput : smallInput
        )}
      >
        <EditorContent editor={editor} />
      </div>
      <div className="flex justify-between mt-4 items-center space-x-2">
        <ChatSources />
        <div
          className={cx(
            'flex items-center transition-colors h-8',
            status !== ChatStatus.DEFAULT || !keywords
              ? 'cursor-not-allowed text-grey6'
              : 'animate-click'
          )}
          onClick={() => void submitQuestion({ _keywords: keywords })}
        >
          <Icon name="IcSendToAi" size="xxlarge_9" />
        </div>
      </div>
    </div>
  );
});

const ChatSources = () => {
  const { t } = useChatTranslation();
  const openSourcesOption = useOpenSourcesOption(SourcesOptionType.CONVERSATION);
  const openSelectAIModel = useOpenSelectAIModel();
  const { style } = useConversationContext();
  const { chatType, setQaModel, switchChatType } = useConversationTools();
  const { qaSearchSource, searchBlocks, model } = useQAQueryOption();

  const isLiteChat = style === ChatUIStyle.LITE;

  const resourcesText = useMemo(() => {
    if (searchBlocks.length > 0) {
      return t('chatPagesSelected', { count: searchBlocks.length });
    }

    if (qaSearchSource === QASearchSource.SPACE) {
      return t('chatInMySpace');
    }
    if (qaSearchSource === QASearchSource.SUBSCRIBE) {
      return t('chatInMySubscriptions');
    }
    if (qaSearchSource === QASearchSource.AI_MODEL) {
      return t('chatAiModelOnly');
    }
    if (qaSearchSource === QASearchSource.HELP) {
      return t('chatInBuildInHelpCenter');
    }

    return t('chatAllResources');
  }, [searchBlocks, qaSearchSource, t]);

  const currentModelText = findAIModel(model).title;

  return (
    <div className="w-full flex justify-between items-center">
      <div className="flex items-center">
        <div
          className={cx(
            'p-0.5 flex items-center truncate text-t4-medium rounded-[20px] bg-aside h-8 space-x-0.5',
            isLiteChat && 'space-x-0'
          )}
        >
          <Tooltip popup={t('search_tip')} className="h-full">
            <div
              className={cx(
                'h-full flex items-center justify-center rounded-full space-x-0.5 cursor-pointer px-1',
                {
                  'bg-white1': chatType === ChatType.SEARCH,
                  'px-4': !isBuildIn(),
                  'w-7': isLiteChat || isMobile,
                  'w-24': !isMobile && !isLiteChat && isBuildIn(), //海外宽度区分，设计要求
                }
              )}
              onClick={() => {
                switchChatType(ChatType.SEARCH);
              }}
            >
              <Icon
                name="IcGlobeShare"
                size="small"
                className={cx(chatType === ChatType.SEARCH ? 'text-ai_color' : 'text-grey3')}
              />
              {!isLiteChat && !isMobile && (
                <span
                  className={cx(
                    'text-t4-medium',
                    chatType === ChatType.SEARCH ? 'text-ai_color' : 'text-grey3'
                  )}
                >
                  {t('chatWebSearch')}
                </span>
              )}
            </div>
          </Tooltip>
          <Tooltip popup={t('space_tip')} className="h-full">
            <div
              className={cx(
                'h-full flex items-center justify-center rounded-full space-x-0.5 cursor-pointer px-1',
                {
                  'bg-white1': chatType === ChatType.SPACE,
                  'px-4': !isBuildIn(),
                  'w-7': isLiteChat || isMobile,
                  'w-24': !isMobile && !isLiteChat && isBuildIn(), //海外宽度区分，设计要求
                }
              )}
              onClick={() => {
                switchChatType(ChatType.SPACE);
              }}
            >
              <Icon
                name="IcSpace"
                size="small"
                className={cx(chatType === ChatType.SPACE ? 'text-ai_color' : 'text-grey3')}
              />
              {!isLiteChat && !isMobile && (
                <span
                  className={cx(
                    'text-t4-medium',
                    chatType === ChatType.SPACE ? 'text-ai_color' : 'text-grey3'
                  )}
                >
                  {t('chatSpace')}
                </span>
              )}
            </div>
          </Tooltip>
          <Tooltip popup={t('ai_model_tip')} className="h-full">
            <div
              className={cx(
                'h-full flex items-center justify-center rounded-full space-x-0.5 cursor-pointer px-1',
                {
                  'bg-white1': chatType === ChatType.AI_MODAL,
                  'px-4': !isBuildIn(), //中文px-4
                  'w-7': isLiteChat || isMobile,
                  'w-24': !isMobile && !isLiteChat && isBuildIn(), //海外宽度区分，设计要求
                }
              )}
              onClick={() => {
                switchChatType(ChatType.AI_MODAL);
              }}
            >
              <Icon
                name="IcAiFill"
                size="small"
                className={cx(
                  'text-t4-medium',
                  chatType === ChatType.AI_MODAL ? 'text-ai_color' : 'text-grey3'
                )}
              />
              {!isLiteChat && !isMobile && (
                <span
                  className={cx(chatType === ChatType.AI_MODAL ? 'text-ai_color' : 'text-grey3')}
                >
                  {t('chat')}
                </span>
              )}
            </div>
          </Tooltip>
        </div>
        {chatType === ChatType.SPACE && (
          <div
            onClick={openSourcesOption}
            className="ml-2 space-x-1 animate-click flex items-center text-ellipsis"
          >
            <span className="text-normal text-t4-medium">{resourcesText}</span>
            <Icon name="IcArrowDown01" size="xxsmall" />
          </div>
        )}
      </div>
      <div
        className="px-2 rounded-[20px] space-x-1 animate-click flex items-center bg-aside text-ellipsis h-8"
        onClick={(e) =>
          openSelectAIModel(e, {
            selectedId: model,
            onSelect: setQaModel,
          })
        }
      >
        <span className="text-normal text-t4-medium">{currentModelText}</span>
        <Icon name="IcArrowDown01" size="xxsmall" />
      </div>
    </div>
  );
};

const SelectPages = () => {
  const { conversationId } = useConversationContext();
  const { toggleSearchBlocs } = useConversationTools();
  const { searchBlocks } = useQAQueryOption();
  const pages = searchPageStore((state) => state.pages);

  const searchPages = useMemo(() => {
    const res = searchBlocks.map((item) => {
      return {
        ...item,
        block: item.uuid ? pages[item.uuid] : undefined,
      };
    });
    return res;
  }, [searchBlocks, pages]);

  if (!searchPages.length) {
    return null;
  }

  return (
    <div className="flex items-center gap-2 flex-wrap text-t5 border-b pb-2 mb-2">
      {searchPages.map((item) => (
        <span
          className="min-w-0 max-w-full flex-shrink border px-1 rounded space-x-1 flex items-center"
          key={item.uuid}
        >
          <span className="truncate">{getPageTitleInfo(item.block).title}</span>
          <Icon
            className="text-grey3 flex-shrink-0"
            name="IcClose"
            size="xxsmall"
            onClick={() => {
              toggleSearchBlocs(item);
              const editor = getEditor(conversationId);
              if (!editor) return;
              editor.state.doc.descendants((node, pos) => {
                if (node.type.name === 'mention' && node.attrs.id === item.uuid) {
                  // 计算节点范围
                  const from = pos;
                  const to = pos + node.nodeSize;
                  // 执行删除
                  editor.commands.deleteRange({ from, to });
                  return false; // 停止遍历
                }
              });
            }}
          />
        </span>
      ))}
    </div>
  );
};
